import React, { CSSProperties, useContext, useEffect, useRef } from 'react';
import { NumericFormat } from 'react-number-format';
import { useSearchParams } from 'react-router-dom';
import { InputAdornment, OutlinedInput, Slider } from '@mui/material';
import GlobalContext from 'contexts/GlobalContext';
import Decimal from 'decimal.js';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useExitToNextTable } from 'hooks/useExitToNextTable';
import { useTableId, useTableSelector } from 'hooks/useTableSelector';
import { removeWaitlistEntry, upsertWaitlistEntry } from 'reducers/waitlist';
import {
  selectIsQueuedFor,
  selectMySpotFor,
  selectMySpotHasValidOffer
} from 'reducers/waitlist.selectors';
import useGameService from 'services/GameService';
import { leaveWaitlist, updateWaitlistAutoBuyIn } from 'services/waitlistService';
import type { WaitlistGameType } from 'types/waitlist';
import { isWaitlistGameType } from 'types/waitlist';
import { clearBuyBackInDeclined, markBuyBackInDeclined } from 'utils/buyBackInDeclined';
import { calculateStepValue, currencyFormatter, toLocaleStringNumber } from 'utils/functions';
import { markPlayerExitedTable } from 'utils/recentlyExitedTables';
import { markRecentlyLeftWaitlist } from 'utils/recentlyLeftWaitlists';

import { CashierContext } from 'components/Cashier/CashierContext';
import GameModal, {
  btnRowStyle,
  infoRowStyle,
  labelStyle,
  primaryBtnStyle,
  secondaryBtnStyle,
  valueStyle
} from 'components/GameModal/GameModal';
import {
  CLAIM_COPY,
  offerDeadlineMs,
  offerSignature,
  PLAIN_JOIN_CLAIM_BUDGET_MS,
  seatTakenMessage
} from 'components/UserActions/seatClaim';
import { useSeatClaim } from 'components/UserActions/useSeatClaim';
import useWaitlistCountdown from 'components/Waitlist/useWaitlistCountdown';

const BuyInModal = () => {
  const rawTablelimit = useTableSelector((table) => table.game.tablelimit);
  const rawTablelow = useTableSelector((table) => table.game.tablelow);
  const rawTablelowOverride = useTableSelector((table) => table.game.tablelowOverride);

  const parseNumericValue = (val: any) => {
    if (typeof val === 'string') {
      return parseFloat(val.toString().replace(/,/g, '')) || 0;
    }
    return parseFloat(val) || 0;
  };

  const tablelimit = parseNumericValue(rawTablelimit);
  const tablelow = parseNumericValue(rawTablelow);
  const tablelowOverride = parseNumericValue(rawTablelowOverride);
  const players = useTableSelector((table) => table.players);
  const gameID = useTableSelector((table) => table.game.gameID);
  // The panel's OWN table id (TableIdContext). `gameID` above is read through
  // useTableSelector, which yields the empty initial state (0) while this
  // table's slice is unhydrated — exactly the window in which a player who
  // has been switching between the grid and a single table taps JOIN TABLE.
  // The join must be addressed to the table the player is looking at.
  const contextTableId = useTableId();
  const hand = useTableSelector((table) => table.game.hand);
  const isPennyTable = useTableSelector((table) => table.game.isPennyTable);
  const bb = useTableSelector((table) => table.game.bb);
  const isMaintenance = useAppSelector((state) => state.maintenanceSchedule.isMaintenance);

  // Waitlist context — when the player reached this modal via a seat offer
  // they are still queued, so the cancel action doubles as "Exit Waitlist"
  // (WL-006A, mirroring Unity BuyInView.UpdateButtonName / ButtonCancelTapped).
  const dispatch = useAppDispatch();
  const gameTypeField = useTableSelector((table) => table.game.gameType);
  const gameVariant = useTableSelector((table) => table.game.game);
  const playerGuid = useAppSelector((state) => state.user.playerGUID) || '';
  const numericGameId = Number(gameID);
  const isQueued = useAppSelector(selectIsQueuedFor(numericGameId));
  const hasValidOffer = useAppSelector(selectMySpotHasValidOffer(numericGameId));
  const mySpot = useAppSelector(selectMySpotFor(numericGameId));
  // Queued but with no live seat offer → the player opened this modal from the
  // "Set Buy-In" CTA to pre-set their auto buy-in. There's no seat to sit into
  // yet, so confirming records the amount and closing does NOT leave the queue.
  const isSettingAutoBuyIn = isQueued && !hasValidOffer;
  // The server's own accept window for this offer, rendered read-only — the same
  // clock the seat-offer prompt and the waitlist chips show. Called
  // unconditionally (a null start time simply reports inactive) so hook order
  // stays stable, and only surfaced when there really is a live offer behind
  // this modal: a plain in-table buy-in and the "Set Buy-In" variant hold
  // nothing, so a hold notice there would be a promise nobody made.
  const { secondsRemaining, active: holdActive } = useWaitlistCountdown({
    waitForAcceptStartTime: mySpot?.waitForAcceptStartTime,
    waitlistTimeOut: mySpot?.waitlistTimeOut ?? 0,
    bumpCount: mySpot?.bumpCount
  });
  const showSeatHold = hasValidOffer && holdActive;
  // The longest time this table's hold line can ever print — its own full accept
  // window. Used only to reserve height (see the notice slot), so the box is
  // sized for the widest sentence it will show rather than the current tick.
  const maxHoldSeconds = mySpot?.waitlistTimeOut ?? 0;
  // Unity selects gameType from GameData.GameType, falling back to the variant
  // enum (JoinButton.tsx mirrors the same chain).
  const normalizedGameType: WaitlistGameType | null = isWaitlistGameType(gameTypeField)
    ? gameTypeField
    : isWaitlistGameType(gameVariant)
    ? gameVariant
    : null;

  const { sitDown, leaveTable } = useGameService();

  const { openCashier } = useContext(CashierContext);
  const exitToNextTable = useExitToNextTable();
  const rawUserBalance = useAppSelector((state) => state.user.playerBankAmount);
  const userBalance = (() => {
    if (typeof rawUserBalance === 'string') {
      return parseFloat((rawUserBalance as string).replace(/,/g, '')) || 0;
    } else if (typeof rawUserBalance === 'number') {
      return rawUserBalance as number;
    }
    return 0;
  })();

  const {
    openAskBuyin,
    setOpenAskBuyin,
    askBuyinTable,
    buyinAmount,
    setBuyinAmount,
    setIsBuyinValueSet,
    openInfoMsg,
    setInfoMsg,
    manuallyAssignedSeatId,
    setManuallyAssignedSeatId,
    setIsJoining,
    isTriggerBuyIn,
    setIsTriggerBuyIn
  } = useContext(GlobalContext);
  // ONE modal, in the panel the player actually acted on. openAskBuyin is
  // app-level in single view, so every keep-alive panel's BuyInModal used to
  // open at once, STACKED — and whichever sat on top answered the click with
  // its own table id, game type, and seat math. That single flaw produced
  // "Table is already full!" on an empty felt, "You are already seated!",
  // "Invalid Seat #N!", and the silent no-op, depending on which panel won
  // (owner RCA, 8/25). The opener now binds askBuyinTable at click time; a
  // panel that is not the bound table renders nothing. A null binding (any
  // opener this migration missed) falls back to the old behaviour rather
  // than locking every player out of buying in.
  const ownTableId = String(contextTableId || gameID || '');
  // When an intent exists, ONLY the panel that can prove it is the bound table
  // renders. A panel that cannot identify itself (ownTableId '') is gated out
  // too — requiring ownTableId !== '' let two simultaneously-unhydrated panels
  // both render, re-opening the stacked-modal hole this exists to close
  // (SQE scan, Marlon 8/25 MED). The bound gameID is never '', so the
  // comparison is decisive.
  const boundElsewhere = !!askBuyinTable && String(askBuyinTable.gameID) !== ownTableId;

  let maxLimit = tablelimit;
  let minLimit = tablelow > 0 ? tablelow : 0;

  if (tablelowOverride > minLimit && !isTriggerBuyIn) minLimit = tablelowOverride;
  if (tablelowOverride > maxLimit && !isTriggerBuyIn) maxLimit = tablelowOverride;

  const sliderStep = isPennyTable ? calculateStepValue(bb) : 1;

  const [searParams] = useSearchParams();
  const isPlayerObserver = searParams.get('observe') === 'yes';
  const availableSeatId = players.findIndex((f: any) => !!f && !f.ID);
  const tableType = useTableSelector((table) => table.game.tabletype);
  maxLimit = isPennyTable
    ? new Decimal(maxLimit).toDP(2).toNumber()
    : Decimal.floor(maxLimit).toNumber();

  // The buy-in ceiling the player can actually afford — the lesser of the table
  // max and their balance. The Min/Half/Max presets and the slider are driven
  // by this, NOT the raw table max, so they never offer an amount the player
  // can't cover (Unity parity). The Table Range label still shows the full
  // table range (minLimit – maxLimit); only the controls cap to the balance.
  const effectiveMax = isPennyTable
    ? new Decimal(Math.min(maxLimit, userBalance)).toDP(2).toNumber()
    : Decimal.floor(Math.min(maxLimit, userBalance)).toNumber();
  const half = isPennyTable
    ? (effectiveMax / 2).toFixed(2)
    : Decimal.ceil(effectiveMax / 2).toNumber();

  // Seed the buy-in amount whenever the dialog opens (and if the effective
  // minimum changes while open, e.g. a re-buy fairness override arrives). When
  // the player is pre-setting their auto buy-in and already saved an amount,
  // restore that amount so reopening the dialog shows what they chose — not the
  // table minimum. Otherwise default to minLimit so a player who just wants the
  // minimum can tap "Buy In" immediately instead of dragging the slider up from
  // 0. minLimit already folds in tablelowOverride (see above), so it's the
  // correct default for both first-time joins and re-buys.
  const savedAutoBuyIn = mySpot?.autoBuyIn ?? 0;
  useEffect(() => {
    if (openAskBuyin) {
      setBuyinAmount(isSettingAutoBuyIn && savedAutoBuyIn > 0 ? savedAutoBuyIn : minLimit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAskBuyin, minLimit]);

  // WL-007: when the player reaches this modal via a seat offer and the offer
  // then expires (they are bumped, or removed from the queue), auto-dismiss the
  // now-stale modal — mirroring Unity BuyInView.ShowWaitlistTimer closing on
  // expiry. We only act once a valid offer has actually been seen while open,
  // so normal/buy-back-in flows (which never carry an offer) are untouched. The
  // accompanying toast is owned by SeatOfferTimeoutSnackbar.
  const hadOfferRef = useRef(false);
  useEffect(() => {
    if (!openAskBuyin) {
      hadOfferRef.current = false;
      return;
    }
    if (hasValidOffer) {
      hadOfferRef.current = true;
      return;
    }
    if (hadOfferRef.current) {
      hadOfferRef.current = false;
      setOpenAskBuyin(false);
      setIsBuyinValueSet(false);
      setInfoMsg(false);
      setBuyinAmount(minLimit);
      setIsTriggerBuyIn(false);
    }
  }, [
    openAskBuyin,
    hasValidOffer,
    minLimit,
    setOpenAskBuyin,
    setIsBuyinValueSet,
    setInfoMsg,
    setBuyinAmount,
    setIsTriggerBuyIn
  ]);

  const handleBuyinSlider = (e: Event) => {
    setBuyinAmount((e?.target as any).value);
    setIsBuyinValueSet(true);
  };

  // Waitlist seat-taking UX: a Buy In tapped while the table is assigning
  // dealer/blinds used to dead-end in an "Unable to take seat" dialog with the
  // request never sent, forcing the player to notice, dismiss and re-tap —
  // burning their 30s offer window on a ~1s condition. The intent is now held
  // and fired the moment this table's own broadcast says it can seat them. The
  // engine's atomic seat UPDATE stays the sole authority on the seat itself.
  const isSeatedHere = useTableSelector((table) => table.currentPlayer?.isSeated === true);
  const seatClaim = useSeatClaim({
    hand,
    players,
    isSeated: isSeatedHere,
    // Revocation must mean the SAME thing here as it does to the WL-007
    // auto-dismiss below, which keys off hasValidOffer (yourTurn &&
    // availableSeats > 0). The signature alone is waitForAcceptStartTime +
    // bumpCount, so another player taking the table's last free seat flips
    // hasValidOffer while leaving the signature identical: the modal dismissed
    // itself while the claim still considered the offer live, and then seated
    // the player into a dialog they had watched close (JRam review). Folding
    // hasValidOffer in makes one condition end both.
    currentOfferSig: hasValidOffer ? offerSignature(mySpot) : null,
    send: (intent) => {
      // Same side effects, in the same order, as the joinable fast path.
      clearBuyBackInDeclined(ownTableId);
      setIsTriggerBuyIn(false);
      setOpenAskBuyin(false);
      setIsBuyinValueSet(true);
      setIsJoining(true);
      // Identity comes from the INTENT (the click-time binding — the authority
      // for which table the player acted on), never re-resolved here: this
      // fires a beat later, by which time the ambient reads may have moved on.
      // Type follows the same rule as the fast path — the live slice wins over
      // the click-time snapshot, which is only the fallback for a slice that
      // was still unhydrated at click time. See services/joinGameParams.
      sitDown(
        intent.gameId,
        intent.seatId,
        intent.amount,
        gameVariant || askBuyinTable?.game || normalizedGameType || intent.gameType
      );
      if (manuallyAssignedSeatId > 0) setManuallyAssignedSeatId(0);
    },
    onResolved: (reason, _intent, alreadySent) => {
      // Owned elsewhere: the WL-007 auto-dismiss effect and
      // SeatOfferTimeoutSnackbar both speak for a revoked offer.
      if (reason === 'offer-revoked') return;
      // Once the request is out, the engine answers it — a successful seating
      // needs no message, and a rejection is already on screen via the
      // table-scoped SeatJoinErrorModal, where adding our copy stacked a second
      // modal saying the same thing.
      if (alreadySent) return;
      // Nothing was sent, so the player is owed an explanation for ANY outcome —
      // 'seated' included. That reason without a send means our own state
      // already read as seated and we never tried, which is reachable from the
      // buy-back-in flow: it emits leaveTable and opens this dialog in the same
      // tick, so isSeated stays true until the next broadcast. Returning early
      // there turned the click into a silent no-op — the exact bug class this
      // whole change set exists to remove (JRam review).
      setInfoMsg(reason === 'seat-taken' ? seatTakenMessage(isQueued) : CLAIM_COPY.failed);
    }
  });

  // A claim must not outlive the dialog that created it. handleClose clears it,
  // but this component stays MOUNTED when the modal closes (only boundElsewhere
  // returns null), so any other close path — the WL-007 auto-dismiss above, or
  // anything added later — would leave the hook ticking with the intent still
  // held, and it would send a seat request for a dialog the player watched close
  // (JRam review). Only a still-HOLDING claim is dropped: one already sent is
  // out of our hands, and its outcome is silenced elsewhere.
  const { phase: claimPhase, clear: clearClaim } = seatClaim;
  useEffect(() => {
    if (!openAskBuyin && claimPhase === 'holding') clearClaim();
  }, [openAskBuyin, claimPhase, clearClaim]);

  const handleClose = () => {
    // Cancelling must not leave a held claim behind to seat them later.
    seatClaim.clear();
    setOpenAskBuyin(false);
    setIsBuyinValueSet(false);
    setInfoMsg(false);
    setBuyinAmount(minLimit);
    setIsTriggerBuyIn(false);
    // Declining an actual seat offer while still queued exits the waitlist
    // (WL-006A). Only when there's a live offer — closing the pre-set "Set
    // Buy-In" dialog must NOT drop the player from the queue.
    if (isQueued && hasValidOffer && normalizedGameType && playerGuid) {
      dispatch(removeWaitlistEntry({ gameId: numericGameId, playerGuid, myGuid: playerGuid }));
      // The DELETE below is fire-and-forget, so the engine keeps broadcasting
      // this spot for up to a poll cycle. That lagging broadcast re-adds the
      // entry we just removed, and because it still reads yourTurn with a seat
      // free, Game.tsx's auto-buy-in effect re-opens THIS modal — the flash the
      // player sees after tapping the X. Stamp the leave so the socket sync
      // drops our own re-added entry until the server confirms it, exactly as
      // useLeaveTable does on the identical optimistic path.
      markRecentlyLeftWaitlist(numericGameId);
      leaveWaitlist({ gameId: numericGameId, gameType: normalizedGameType, playerGuid }).catch(
        (err) => console.error('[waitlist] leaveWaitlist on buy-in close failed', err)
      );
    }
    if (isTriggerBuyIn) {
      // Idle buy-back-in: the player declined the forced rebuy. Remember it so
      // re-opening this table doesn't re-fire the prompt (Unity parity — the
      // player observes and sees the normal Join Table button instead of being
      // trapped in a rebuy loop). Then stand up and return to the lobby.
      // All three name the panel's own table (ownTableId), the one leaveTable
      // unsubscribes. The selector's gameID can still be 0 or the last table
      // to broadcast while this slice is unhydrated, which would hide or close
      // the wrong table and leave this one in the nav.
      markBuyBackInDeclined(ownTableId);
      // The player's own exit, once the leave was sent (see useLeaveTable).
      if (leaveTable(true)) markPlayerExitedTable(ownTableId);
      // Same as the top-right Exit Table: close this table and hop to the next
      // open one, only returning to the lobby when it was the last table.
      exitToNextTable(ownTableId);
    }
  };

  const handleBuyIn = () => {
    if (hand === '' || +hand > 3) {
      if (buyinAmount > 0) {
        // A real rebuy clears the declined flag so future idle sessions can
        // prompt again.
        clearBuyBackInDeclined(ownTableId);
        setIsTriggerBuyIn(false);
        setOpenAskBuyin(false);
        setIsBuyinValueSet(true);
        // Pre-setting buy-in while waitlisted (no seat yet): record it as my
        // auto buy-in so I'm seated for this amount when my turn comes, rather
        // than sitting down now.
        if (isSettingAutoBuyIn) {
          if (mySpot && normalizedGameType && playerGuid) {
            dispatch(
              upsertWaitlistEntry({ playerGuid, entry: { ...mySpot, autoBuyIn: buyinAmount } })
            );
            updateWaitlistAutoBuyIn({
              gameId: numericGameId,
              gameType: normalizedGameType,
              playerGuid,
              autoBuyIn: buyinAmount
            }).catch((err) => console.error('[waitlist] updateWaitlistAutoBuyIn failed', err));
          }
          return;
        }
        setIsJoining(true);
        // A seat of 0 means "the client could not resolve one" — the engine
        // then picks the first free seat itself (userJoinGame). Previously
        // neither branch matched when `availableSeatId` came back -1 from an
        // unhydrated `players` array, so the click emitted NOTHING while the
        // modal had already closed and set isJoining: the reported "press
        // Buy In and nothing happens, you're not sat" after switching views.
        const seatToTake = manuallyAssignedSeatId > 0 ? manuallyAssignedSeatId : availableSeatId;
        // The game type travels with the join for the same reason the table id
        // does: the engine routes and queries on it, and the ambient fallback
        // goes stale during rapid table switching. This panel's slice knows its
        // own variant once hydrated; the waitlist entry knows it before that.
        // The bound intent is the identity of record; the panel's own reads
        // are the fallback for a null binding only.
        // Identity: the click-time binding is the authority for WHICH table.
        // Type: the LIVE slice wins over the click-time snapshot — the render
        // gate guarantees this panel IS the bound table, so its hydrated
        // variant is that table's truth, and the snapshot (possibly bound
        // while unhydrated) only covers a slice still empty at submit
        // (SQE scan, Marlon 8/25 HIGH).
        sitDown(
          askBuyinTable?.gameID || contextTableId || gameID,
          seatToTake > 0 ? seatToTake : 0,
          buyinAmount,
          gameVariant || askBuyinTable?.game || normalizedGameType
        );
        if (manuallyAssignedSeatId > 0) {
          setManuallyAssignedSeatId(0);
        }
      } else {
        setInfoMsg(true);
      }
    } else if (buyinAmount <= 0) {
      // Same validation the joinable path applies first — an empty buy-in is the
      // player's to fix, and has nothing to do with the table phase.
      setInfoMsg(true);
    } else if (isSettingAutoBuyIn) {
      // Pre-setting an auto buy-in seats nobody, so the table phase is
      // irrelevant here — but this branch predates the claim work and is left
      // exactly as it was rather than widened in a UX fix. Tracked separately.
      setInfoMsg('Unable to take seat. Please try again.');
    } else {
      // The table is mid-setup (dealer/blinds/straddle assignment — GAME_HAND
      // 0–3), so a new player can't be seated YET. Rather than dropping the
      // request behind a terminal dialog, hold the intent and let useSeatClaim
      // fire it as soon as the table is joinable. The modal deliberately stays
      // OPEN so the pending state lives in its own footer instead of a second
      // modal stacked on top of it (unusable on a ~360px viewport).
      // Seat/identity/type resolve exactly as the joinable fast path does, and
      // are captured NOW: this is the click the player made, on the table they
      // were looking at. An unresolved seat is held as 0 — "the engine picks"
      // (joinGameParams Rule 2) — never turned into a failure message, which
      // would re-create the silent-no-op class of bug that rule exists to fix.
      const seatToTake = manuallyAssignedSeatId > 0 ? manuallyAssignedSeatId : availableSeatId;
      // Offer-driven claims are bounded by the SERVER's offer window; a plain
      // in-table join gets a short local retry budget instead.
      const deadline = hasValidOffer ? offerDeadlineMs(mySpot) : null;
      seatClaim.hold({
        gameId: askBuyinTable?.gameID || contextTableId || gameID,
        seatId: seatToTake > 0 ? seatToTake : 0,
        amount: buyinAmount,
        gameType: gameVariant || askBuyinTable?.game || normalizedGameType,
        offerSig: deadline === null ? null : offerSignature(mySpot),
        deadlineMs: deadline ?? Date.now() + PLAIN_JOIN_CLAIM_BUDGET_MS
      });
    }
  };

  const handleBuyinTextField = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsBuyinValueSet(true);
    setBuyinAmount((event?.target as any).value);
  };

  const handlePasteValue = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedValue = Number(event.clipboardData.getData('text'));
    setBuyinAmount(pastedValue);
  };

  const isOpen =
    !isMaintenance && tableType !== 'm' && tableType !== 't' && !isPlayerObserver && openAskBuyin;
  const canAfford = userBalance >= minLimit;
  const isDisabled =
    buyinAmount > userBalance || buyinAmount < minLimit || buyinAmount > effectiveMax;

  // Add Funds opens the in-app Cashier on the Deposit tab (it overlays this
  // modal at a higher z-index), rather than redirecting to the external account
  // site in a new tab. We deliberately don't tear down the Buy-In modal here:
  // closing it runs the waitlist-exit side effects in handleClose, and the
  // player should return to a now-affordable Buy-In once funds land.
  const handleAddFunds = () => openCashier('deposit');

  if (boundElsewhere) return null;

  return (
    <>
      <GameModal
        open={isOpen}
        onClose={handleClose}
        // Buy Back In (idle forced rebuy) has no X — the only ways out are
        // Buy In or Exit Table (Unity parity). The X used to send the player
        // to the lobby yet leave them re-prompted on re-open.
        showClose={!isTriggerBuyIn}
        // …and for the same reason a backdrop tap must not dismiss it either:
        // that is the X by another name, and would re-open the prompt on the
        // player's next visit instead of resolving it. The ordinary Buy-In
        // keeps the default tap-outside-to-close.
        closeOnBackdropClick={!isTriggerBuyIn}
        title={isTriggerBuyIn ? 'Buy Back In' : 'Buy-In'}>
        {/* Fairness policy or idle message */}
        {minLimit > tablelow ? (
          <div style={messageStyle}>
            In accordance with our fairness policy, players returning within one hour must rejoin
            with the same chip count.
          </div>
        ) : isTriggerBuyIn ? (
          <div style={messageStyle}>
            The game has been idle for more than 2 minutes. Buy-ins will be reset.
          </div>
        ) : null}

        {/* Table info */}
        <div>
          <div style={infoRowStyle}>
            <span style={labelStyle}>Table Range</span>
            <span style={valueStyle}>
              ${toLocaleStringNumber(minLimit)} – ${toLocaleStringNumber(maxLimit)}
            </span>
          </div>
          <div style={infoRowStyle}>
            <span style={labelStyle}>Your Balance</span>
            <span style={{ ...valueStyle, color: canAfford ? '#a8d88e' : '#e05555' }}>
              {currencyFormatter(userBalance)}
            </span>
          </div>
        </div>

        {canAfford ? (
          <>
            {/* Preset buttons */}
            <div style={presetRowStyle}>
              <button onClick={() => setBuyinAmount(minLimit)} style={presetBtnStyle}>
                <span style={presetLabelStyle}>Min</span>
                <span style={presetValueStyle}>${toLocaleStringNumber(minLimit)}</span>
              </button>
              <button
                onClick={() => setBuyinAmount(+half)}
                style={{
                  ...presetBtnStyle,
                  opacity: +half < minLimit ? 0.4 : 1
                }}
                disabled={+half < minLimit}>
                <span style={presetLabelStyle}>Half</span>
                <span style={presetValueStyle}>${toLocaleStringNumber(half)}</span>
              </button>
              <button onClick={() => setBuyinAmount(effectiveMax)} style={presetBtnStyle}>
                <span style={presetLabelStyle}>Max</span>
                <span style={presetValueStyle}>${toLocaleStringNumber(effectiveMax)}</span>
              </button>
            </div>

            {/* Slider */}
            <Slider
              value={buyinAmount}
              onChange={(e) => handleBuyinSlider(e as any)}
              step={sliderStep}
              max={+effectiveMax}
              min={+minLimit}
              sx={{
                color: '#E98C26',
                '& .MuiSlider-thumb': {
                  width: 20,
                  height: 20,
                  backgroundColor: '#d4a853',
                  boxShadow: '0 0 8px rgba(233, 140, 38, 0.4)'
                },
                '& .MuiSlider-track': {
                  background: 'linear-gradient(90deg, #E98C26, #d47a1a)'
                },
                '& .MuiSlider-rail': {
                  backgroundColor: 'rgba(255,255,255,0.12)'
                }
              }}
            />

            {/* Amount input */}
            <NumericFormat
              customInput={OutlinedInput}
              value={buyinAmount}
              onChange={handleBuyinTextField}
              onPaste={handlePasteValue}
              decimalScale={isPennyTable ? 2 : 0}
              allowNegative={false}
              startAdornment={<InputAdornment position='start'>$</InputAdornment>}
              fullWidth
              sx={{
                backgroundColor: 'rgba(255,255,255,0.06)',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '1.2rem',
                fontWeight: 700,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.1)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(233, 140, 38, 0.3)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E98C26'
                },
                '& .MuiInputAdornment-root': {
                  color: 'rgba(255,255,255,0.4)'
                }
              }}
            />
          </>
        ) : (
          <div style={messageStyle}>
            Your balance of {currencyFormatter(userBalance)} is below the minimum buy-in of{' '}
            {currencyFormatter(minLimit)}. Please add funds to continue.
          </div>
        )}

        {/* One notice region directly above the actions, shared by the two
            things this modal has to say about the seat. It shows the seat-hold
            line while the offer is simply counting down, and the claim status
            once the player has tapped Buy In — the second SWAPS the first out
            rather than stacking under it.

            The slot is always rendered, and its height comes from a hidden copy
            of the LONGEST message it can hold. So swapping between the two
            messages (and to nothing at all) never resizes the card — appearing
            only while claiming used to grow it by two lines plus a flex gap and
            move the buttons under the player's thumb mid-tap. A hidden ghost
            beats a fixed minHeight because it measures the real copy at the real
            width, so nothing clips on a narrow screen. Only the variants that
            can actually claim reserve it; the can't-afford variant has no Buy In
            button, so there is no state change to absorb there. */}
        {canAfford || seatClaim.isClaiming ? (
          <div style={noticeSlotStyle}>
            {/* Height-setters: hidden copies of BOTH messages this slot can
                show, each at its longest, so the slot is the same height in
                every state — either message, the swap between them, or nothing.
                The hold line's widest form is its own table's full accept
                window, so this is measured rather than guessed. */}
            <div style={noticeGhostStyle} aria-hidden data-testid='buyin-notice-reserve'>
              {CLAIM_COPY.retrying}
            </div>
            <div style={noticeGhostStyle} aria-hidden data-testid='buyin-notice-reserve-hold'>
              {holdSentence(formatHoldRemaining(maxHoldSeconds))}
            </div>
            {seatClaim.isClaiming ? (
              <div style={noticeLiveStyle} role='status' data-testid='buyin-notice'>
                {CLAIM_COPY.retrying}
              </div>
            ) : showSeatHold ? (
              <div style={noticeLiveStyle} data-testid='buyin-notice'>
                {holdSentence(formatHoldRemaining(secondsRemaining))}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Action buttons */}
        <div style={btnRowStyle}>
          <button onClick={handleClose} style={secondaryBtnStyle}>
            {isSettingAutoBuyIn
              ? 'Cancel'
              : isQueued
              ? 'Exit Waitlist'
              : isTriggerBuyIn
              ? 'Exit Table'
              : 'Cancel'}
          </button>
          {canAfford ? (
            <button
              onClick={handleBuyIn}
              disabled={isDisabled || seatClaim.isClaiming}
              aria-busy={seatClaim.isClaiming}
              style={{
                ...primaryBtnStyle,
                opacity: isDisabled || seatClaim.isClaiming ? 0.4 : 1,
                cursor: isDisabled || seatClaim.isClaiming ? 'not-allowed' : 'pointer',
                // "Claiming Seat" is materially longer than "Buy In": on a
                // ~360px card it would wrap to two lines and make the button —
                // and therefore the whole card — taller, which is the same jump
                // the reserved status line above exists to prevent. Hold it on
                // one line and buy the width back from the horizontal padding
                // rather than the label.
                whiteSpace: 'nowrap',
                padding: '0.75rem 1rem'
              }}>
              {seatClaim.isClaiming ? CLAIM_COPY.pending : isSettingAutoBuyIn ? 'Save' : 'Buy In'}
            </button>
          ) : (
            <button onClick={handleAddFunds} style={{ ...primaryBtnStyle, flex: 1 }}>
              Add Funds
            </button>
          )}
        </div>
      </GameModal>

      {/* Info dialog for zero buy-in attempt or straddle block */}
      <GameModal open={!!openInfoMsg} onClose={() => setInfoMsg(false)} title='Information'>
        <div style={messageStyle}>
          {typeof openInfoMsg === 'string' ? openInfoMsg : 'Please add more chips to your buy-in.'}
        </div>
        <div style={btnRowStyle}>
          <button onClick={() => setInfoMsg(false)} style={secondaryBtnStyle}>
            Close
          </button>
        </div>
      </GameModal>
    </>
  );
};

export default BuyInModal;

/**
 * How long the seat is still held, phrased for the length actually left. Under a
 * minute — which is the whole 30s cash-waitlist window — plain seconds read
 * better than "0:24 minutes"; the m:ss form is kept for the case where a table
 * is configured with a longer accept window.
 */
export const formatHoldRemaining = (seconds: number): string => {
  const total = Math.max(0, Math.ceil(seconds));
  if (total < 60) return `${total} second${total === 1 ? '' : 's'}`;
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, '0')} minutes`;
};

/**
 * The notice box. Borrows the branded "Tournament Info" container (amber wash,
 * gold hairline, 10px radius) but reads as a sentence rather than a control:
 * left-aligned, regular weight, ordinary padding — no centring, no button
 * minimum height, no pointer.
 */
const noticeBoxStyle: CSSProperties = {
  background: 'rgba(233, 140, 38, 0.12)',
  border: '1px solid rgba(233, 140, 38, 0.6)',
  borderRadius: '10px',
  padding: '0.7rem 0.9rem',
  fontSize: '0.85rem',
  lineHeight: 1.45,
  textAlign: 'left',
  color: 'rgba(255, 255, 255, 0.78)'
};

/**
 * The seat-hold sentence, with its time value emphasised. Shared by the live
 * line and its hidden height-setter so both wrap identically — two copies of the
 * markup could drift and the reserved height would stop matching.
 */
const holdSentence = (time: string) => (
  <>
    We&apos;re holding this seat for you for <strong style={seatHoldTimeStyle}>{time}</strong>
  </>
);

/**
 * Stacks the hidden height-setters and the live message in ONE grid cell. The
 * row is as tall as the tallest of them, so the slot — and therefore the card —
 * is identical in every state.
 *
 * Centring the box between the amount input and the buttons is a MARGIN job, not
 * an alignment one. The body is a flex column with a uniform 0.75rem gap, but
 * btnRowStyle adds its own 0.5rem marginTop — so the space below this slot was
 * 1.25rem against 0.75rem above, and the box sat noticeably closer to the input.
 * Matching that 0.5rem here makes both gaps 1.25rem.
 *
 * `alignItems: center` is a separate guard: it only bites at a width where the
 * two messages wrap to different heights, and centres the shorter one in the
 * reserved space instead of letting it hug the top edge.
 */
const noticeSlotStyle: CSSProperties = {
  display: 'grid',
  alignItems: 'center',
  marginTop: '0.5rem'
};

/** Hidden, but occupies the space — see noticeSlotStyle. */
const noticeGhostStyle: CSSProperties = {
  ...noticeBoxStyle,
  gridArea: '1 / 1',
  visibility: 'hidden'
};

const noticeLiveStyle: CSSProperties = {
  ...noticeBoxStyle,
  gridArea: '1 / 1'
};

const seatHoldTimeStyle: CSSProperties = {
  color: '#E98C26',
  fontWeight: 700,
  // The seconds tick every second; tabular figures stop the sentence jittering
  // as digit widths change.
  fontVariantNumeric: 'tabular-nums'
};

const messageStyle: CSSProperties = {
  fontSize: '0.85rem',
  color: 'rgba(255, 255, 255, 0.6)',
  lineHeight: 1.5,
  textAlign: 'center'
};

const presetRowStyle: CSSProperties = {
  display: 'flex',
  gap: '0.5rem'
};

const presetBtnStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.15rem',
  padding: '0.5rem',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '10px',
  background: 'rgba(255, 255, 255, 0.04)',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'all 0.15s ease'
};

const presetLabelStyle: CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.4)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const presetValueStyle: CSSProperties = {
  fontSize: '0.85rem',
  fontWeight: 700,
  color: 'rgba(255, 255, 255, 0.9)'
};
